import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const BookTable = ({ books, onEdit, onDelete, onBulkSelect, selectedIds, onSelectAll }) => {
  const [sortField, setSortField] = useState('title');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedBooks = [...(books || [])]?.sort((a, b) => {
    let aVal = a?.[sortField] ?? '';
    let bVal = b?.[sortField] ?? '';
    if (typeof aVal === 'string') aVal = aVal?.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal?.toLowerCase();
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const allSelected = books?.length > 0 && selectedIds?.length === books?.length;

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    return sortDir === 'asc'
      ? <Icon name="ChevronUp" size={14} className="text-primary" />
      : <Icon name="ChevronDown" size={14} className="text-primary" />;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th className="px-4 py-3 text-left w-10">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => onSelectAll?.(e?.target?.checked)}
                className="rounded border-border"
                aria-label="Select all books"
              />
            </th>
            <th className="px-4 py-3 text-left w-16">Cover</th>
            <th className="px-4 py-3 text-left">
              <button onClick={() => handleSort('title')} className="flex items-center gap-1 font-medium hover:text-primary transition-colors">
                Title <SortIcon field="title" />
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <button onClick={() => handleSort('author')} className="flex items-center gap-1 font-medium hover:text-primary transition-colors">
                Author <SortIcon field="author" />
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <button onClick={() => handleSort('category')} className="flex items-center gap-1 font-medium hover:text-primary transition-colors">
                Category <SortIcon field="category" />
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <button onClick={() => handleSort('price')} className="flex items-center gap-1 font-medium hover:text-primary transition-colors">
                Price <SortIcon field="price" />
              </button>
            </th>
            <th className="px-4 py-3 text-left">Status</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedBooks?.length === 0 ? (
            <tr>
              <td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">
                <div className="flex flex-col items-center gap-2">
                  <Icon name="BookOpen" size={40} className="opacity-30" />
                  <p>No books found</p>
                </div>
              </td>
            </tr>
          ) : (
            sortedBooks?.map((book) => (
              <tr key={book?.id} className="border-b border-border hover:bg-muted/20 transition-colors">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds?.includes(book?.id)}
                    onChange={(e) => onBulkSelect?.(book?.id, e?.target?.checked)}
                    className="rounded border-border"
                    aria-label={`Select ${book?.title}`}
                  />
                </td>
                <td className="px-4 py-3">
                  {book?.cover_image ? (
                    <img
                      src={book?.cover_image}
                      alt={book?.cover_image_alt || `${book?.title} cover`}
                      className="w-10 h-14 object-cover rounded shadow-sm"
                    />
                  ) : (
                    <div className="w-10 h-14 bg-muted rounded flex items-center justify-center">
                      <Icon name="BookOpen" size={16} className="text-muted-foreground" />
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="max-w-[200px]">
                    <p className="font-medium text-foreground line-clamp-2">{book?.title}</p>
                    {book?.is_new && (
                      <span className="inline-block mt-1 px-1.5 py-0.5 bg-success/10 text-success text-xs rounded">New</span>
                    )}
                    {book?.is_bestseller && (
                      <span className="inline-block mt-1 ml-1 px-1.5 py-0.5 bg-accent/10 text-accent text-xs rounded">Bestseller</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{book?.author}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full capitalize">
                    {book?.category || 'Uncategorized'}
                  </span>
                </td>
                <td className="px-4 py-3 font-medium">${Number(book?.price || 0)?.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    book?.is_new ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                  }`}>
                    {book?.is_new ? 'Active' : 'Standard'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEdit?.(book)}
                      className="p-1.5 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                      aria-label={`Edit ${book?.title}`}
                    >
                      <Icon name="Pencil" size={16} />
                    </button>
                    <button
                      onClick={() => onDelete?.(book)}
                      className="p-1.5 rounded hover:bg-error/10 text-muted-foreground hover:text-error transition-colors"
                      aria-label={`Delete ${book?.title}`}
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default BookTable;
